declare global {
  interface Window {
    TiktokAnalyticsObject?: string;
    ttq?: any;
  }
}

let initialized = false;
let scriptLoaded = false;

const isProductionDomain = (): boolean => {
  if (typeof window === 'undefined') return false;

  const currentDomain = window.location.hostname;
  const allowedDomains = ['buunpo.com', 'www.buunpo.com', 'localhost'];

  return allowedDomains.includes(currentDomain);
};

const sha256 = async (message: string): Promise<string> => {
  const msgBuffer = new TextEncoder().encode(message.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

export interface UserData {
  email?: string;
  phone?: string;
  external_id?: string;
}

const waitForScriptLoad = (): Promise<void> => {
  return new Promise((resolve) => {
    if (scriptLoaded) {
      resolve();
      return;
    }

    const checkScript = setInterval(() => {
      if (window.ttq && window.ttq.context) {
        clearInterval(checkScript);
        scriptLoaded = true;
        resolve();
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkScript);
      scriptLoaded = true;
      resolve();
    }, 10000);
  });
};

export const initTikTokPixel = async () => {
  if (!isProductionDomain()) {
    return;
  }

  if (initialized) return;

  const pixelId = 'D50PGPRC77U621TEP3T0';

  (function (w: any, d: Document, t?: string) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t as string] = w[t as string] || []);
    ttq.methods = [
      'page',
      'track',
      'identify',
      'instances',
      'debug',
      'on',
      'off',
      'once',
      'ready',
      'alias',
      'group',
      'enableCookie',
      'disableCookie',
    ];
    ttq.setAndDefer = function (target: any, method: string) {
      target[method] = function (...args: any[]) {
        target.push([method].concat(args));
      };
    };
    ttq.instance = function (pixelIdParam: string) {
      const instance = ttq._i[pixelIdParam] || [];
      for (let n = 0; n < ttq.methods.length; n++) {
        ttq.setAndDefer(instance, ttq.methods[n]);
      }
      return instance;
    };
    ttq.load = function (pixelIdParam: string, options?: any) {
      const baseUrl = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[pixelIdParam] = [];
      ttq._i[pixelIdParam]._u = baseUrl;
      ttq._t = ttq._t || {};
      ttq._t[pixelIdParam] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[pixelIdParam] = options || {};
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = baseUrl + '?sdkid=' + pixelIdParam + '&lib=' + t;
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      }
    };

    ttq.load(pixelId);
  })(window, document, 'ttq');

  initialized = true;
  await waitForScriptLoad();
};

export const setUserData = async (userData: UserData) => {
  if (!isProductionDomain() || !window.ttq) return;

  await waitForScriptLoad();

  try {
    const identifyData: any = {};

    if (userData.email) {
      identifyData.sha256_email = await sha256(userData.email);
    }

    if (userData.phone) {
      const phoneClean = userData.phone.replace(/\D/g, '');
      identifyData.sha256_phone_number = await sha256(phoneClean);
    }

    if (userData.external_id) {
      identifyData.external_id = userData.external_id;
    }

    if (Object.keys(identifyData).length > 0) {
      window.ttq.identify(identifyData);
    }
  } catch (error) {
    console.error(error);
  }
};

export const trackTikTokEvent = async (
  event: string,
  payload?: Record<string, unknown>
) => {
  if (!isProductionDomain()) {
    return;
  }

  if (typeof window === 'undefined' || !window.ttq) {
    return;
  }

  await waitForScriptLoad();

  try {
    window.ttq.track(event, payload);
  } catch (error) {
    console.error(error);
  }
};
